import React, { useState, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Chart
} from 'chart.js';
import { useOptimizationStore } from '../store/optimizationStore';
import EmbeddingVisualizationSVG from './EmbeddingVisualizationSVG';
import IterationVisualizationSVG from './IterationVisualizationSVG';
import './GraphsSection.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface GraphsSectionProps {
  isOptimizing?: boolean;
  progress?: number;
  isMultiAgent?: boolean; // Новый пропс для мультиагентной страницы
  isConnector?: boolean; // Новый пропс для ConnectorPage
  /** Connector: requested optimization iterations (graph axis 0..N). */
  requestedIterations?: number;
  /** Метрика отбора (primary_metric_id/selection_scenario) — подписываем ей график Evaluation,
   * иначе непонятно, что на нём реально лежит: качество, токены или что-то ещё. */
  primaryMetricId?: string;
}

/** "tokens" -> "Токены", "actor_tokens" -> "Токены актора" и т.п. */
function formatMetricLabel(metricId: string): string {
  const words = metricId.trim().replace(/[_-]+/g, ' ');
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const GraphsSection: React.FC<GraphsSectionProps> = ({
  isOptimizing = false,
  progress = 0,
  isMultiAgent = false,
  isConnector = false,
  requestedIterations,
  primaryMetricId,
}) => {
  const evaluationTitle = primaryMetricId ? formatMetricLabel(primaryMetricId) : 'Оценка';
  // Quality-график дублирует соседний график метрики отбора, когда метрика и есть quality —
  // показываем его только тогда, когда отбор идёт не по качеству.
  const showQualityGraph = primaryMetricId !== 'quality';
  const {
    embeddingData,
    iterationData,
    iterations,
    multiAgentIterations,
    graphSliderValue,
    multiAgentGraphSliderValue,
    selectedIterationIndex,
    iterationResults,
    // Мультиагентные данные
    multiAgentIterationResults,
    multiAgentIsOptimized,
    multiAgentSelectedIterationIndex,
    // Connector Multi-Agent данные
    connectorMultiAgentIterationResults,
    connectorMultiAgentIsOptimized,
    connectorMultiAgentSelectedIterationIndex,
    connectorMultiAgentGraphSliderValue
  } = useOptimizationStore();

  // Выбираем правильные данные в зависимости от типа страницы
  const currentIterationResults = isConnector 
    ? connectorMultiAgentIterationResults 
    : (isMultiAgent ? multiAgentIterationResults : iterationResults);
  const currentIsOptimized = isConnector 
    ? connectorMultiAgentIsOptimized 
    : (isMultiAgent ? multiAgentIsOptimized : (currentIterationResults.length > 0));
  const currentSelectedIterationIndex = isConnector 
    ? connectorMultiAgentSelectedIterationIndex 
    : (isMultiAgent ? multiAgentSelectedIterationIndex : selectedIterationIndex);
  const currentIterations = isConnector
    ? (requestedIterations != null && requestedIterations > 0 ? requestedIterations : iterations)
    : isMultiAgent
      ? multiAgentIterations
      : iterations;
  const currentGraphSliderValue = isConnector 
    ? connectorMultiAgentGraphSliderValue 
    : (isMultiAgent ? multiAgentGraphSliderValue : graphSliderValue);

  const graphAccentColor = isConnector ? '#FF8300' : '#0099FA';
  const sectionClassName = isConnector
    ? 'graphs-section connector-graphs-section'
    : 'graphs-section';

  // Показываем индикатор только во время оптимизации
  const shouldShowProgress = isOptimizing;
  const displayProgress = shouldShowProgress ? progress : 0;
  
  // 🐛 Debug: логируем изменения прогресса
  useEffect(() => {
    if (shouldShowProgress) {
      console.log('📊 GraphsSection progress update:', {
        isOptimizing,
        progress,
        displayProgress,
        isMultiAgent
      });
    }
  }, [isOptimizing, progress, displayProgress, shouldShowProgress, isMultiAgent]);



  const [activeMobileGraph, setActiveMobileGraph] = useState(0); // 0: Evaluation, 1: Embedding
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // --- Масштабирование для каждого графика ---
  const evalContainerRef = useRef<HTMLDivElement>(null);
  const evalGraphRef = useRef<HTMLDivElement>(null);
  const embedContainerRef = useRef<HTMLDivElement>(null);
  const embedGraphRef = useRef<HTMLDivElement>(null);
  const [evalScale, setEvalScale] = useState(1);
  const [embedScale, setEmbedScale] = useState(1);

  const updateGraphScale = (
    container: HTMLDivElement | null,
    graph: HTMLDivElement | null,
    setScale: React.Dispatch<React.SetStateAction<number>>
  ) => {
    if (!container || !graph) return;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const graphWidth = graph.scrollWidth;
    const graphHeight = graph.scrollHeight;
    let scale = 1;
    if (graphWidth > containerWidth || graphHeight > containerHeight) {
      scale = Math.min(containerWidth / graphWidth, containerHeight / graphHeight, 1);
    }
    setScale(scale);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate graph scale when container size changes (e.g. NLT panel opens).
  useEffect(() => {
    const observers: ResizeObserver[] = [];

    const observePair = (
      container: HTMLDivElement | null,
      graph: HTMLDivElement | null,
      setScale: React.Dispatch<React.SetStateAction<number>>
    ) => {
      if (!container) return;
      const run = () => updateGraphScale(container, graph, setScale);
      run();
      const observer = new ResizeObserver(run);
      observer.observe(container);
      if (graph) observer.observe(graph);
      observers.push(observer);
    };

    observePair(evalContainerRef.current, evalGraphRef.current, setEvalScale);
    observePair(embedContainerRef.current, embedGraphRef.current, setEmbedScale);

    return () => observers.forEach((observer) => observer.disconnect());
  }, [
    isMobile,
    activeMobileGraph,
    iterationData,
    embeddingData,
    currentIterations,
    currentGraphSliderValue,
    displayProgress,
    currentIterationResults,
  ]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  // 0: Evaluation, 1: Embedding, 2: Quality (только когда метрика отбора не quality)
  const mobileGraphCount = showQualityGraph ? 3 : 2;

  // Метрика отбора может смениться на "quality" в рантайме — тогда Quality-слайд исчезает,
  // а активный индекс мог указывать на него.
  useEffect(() => {
    setActiveMobileGraph((current) => (current >= mobileGraphCount ? mobileGraphCount - 1 : current));
  }, [mobileGraphCount]);

  const handleTouchEnd = () => {
    const dx = touchEndX.current - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0 && activeMobileGraph < mobileGraphCount - 1) {
        setSlideDirection('left');
        const next = activeMobileGraph + 1;
        setTimeout(() => {
        setActiveMobileGraph(next);
        }, 250); // Увеличили задержку для более плавной анимации
      }
      if (dx > 0 && activeMobileGraph > 0) {
        setSlideDirection('right');
        const prev = activeMobileGraph - 1;
        setTimeout(() => {
        setActiveMobileGraph(prev);
        }, 250); // Увеличили задержку для более плавной анимации
      }
    }
  };

  // Сброс анимации после смены графика
  React.useEffect(() => {
    if (slideDirection) {
      const timeout = setTimeout(() => setSlideDirection(null), 500);
      return () => clearTimeout(timeout);
    }
  }, [slideDirection, activeMobileGraph]);

  return (
    <section className={sectionClassName}>
      {isMobile ? (
        <div className="mobile-graphs-swiper">
          <div
            className={`mobile-graph-slide ${slideDirection === 'left' ? 'slide-left' : ''} ${slideDirection === 'right' ? 'slide-right' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
          >
            {activeMobileGraph === 0 ? (
              <div className="graph-container" ref={evalContainerRef}>
                {shouldShowProgress && (
                  <div className="circular-progress-container" data-label="Прогресс по итерациям">
                    <div className={`circular-progress ${isOptimizing ? 'active' : ''}`} style={{ '--progress': `${displayProgress}%` } as React.CSSProperties}>
                      <div className="circular-progress-value">{displayProgress.toFixed(1)}%</div>
                    </div>
                  </div>
                )}
                <h3 className="graph-title mobile-graph-title">{evaluationTitle}<br/><span className="graph-title-sub">Визуализация</span></h3>
                <div className="graph" ref={evalGraphRef} style={{ transform: `scale(${evalScale})`, transformOrigin: 'top left', width: '100%', height: '100%' }}>
                  <IterationVisualizationSVG
                    iterationData={iterationData}
                    iterations={currentIterations}
                    graphSliderValue={currentGraphSliderValue}
                    baseColor={graphAccentColor}
                    optimizationProgress={displayProgress}
                    iterationResults={currentIterationResults}
                    isMultiAgent={isMultiAgent}
                  />
                </div>
              </div>
            ) : activeMobileGraph === 1 ? (
              <div className="graph-container" ref={embedContainerRef}>
                {shouldShowProgress && (
                  <div className="circular-progress-container" data-label="Движение эмбеддингов">
                    <div className={`circular-progress ${isOptimizing ? 'active' : ''}`} style={{ '--progress': `${displayProgress}%` } as React.CSSProperties}>
                      <div className="circular-progress-value">{displayProgress.toFixed(1)}%</div>
                    </div>
                  </div>
                )}
                <h3 className="graph-title mobile-graph-title">Эмбеддинги<br/><span className="graph-title-sub">Визуализация</span></h3>
                <div className="graph" ref={embedGraphRef} style={{ transform: `scale(${embedScale})`, transformOrigin: 'top left', width: '100%', height: '100%' }}>
                  <EmbeddingVisualizationSVG
                    embeddingData={embeddingData}
                    iterations={currentIterations}
                    graphSliderValue={currentGraphSliderValue}
                    baseColor={graphAccentColor}
                    optimizationProgress={displayProgress}
                    iterationResults={currentIterationResults}
                    isMultiAgent={isMultiAgent}
                  />
                </div>
              </div>
            ) : showQualityGraph ? (
              <div className="graph-container">
                <h3 className="graph-title mobile-graph-title">Качество<br/><span className="graph-title-sub">Визуализация</span></h3>
                <div className="graph">
                  <IterationVisualizationSVG
                    iterationData={[]}
                    iterations={currentIterations}
                    graphSliderValue={currentGraphSliderValue}
                    baseColor={graphAccentColor}
                    optimizationProgress={displayProgress}
                    iterationResults={currentIterationResults}
                    isMultiAgent={isMultiAgent}
                    metricKey="quality_point"
                    yAxisLabel="Качество"
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className="mobile-graphs-indicators">
            {[evaluationTitle, 'Эмбеддинги', ...(showQualityGraph ? ['Качество'] : [])].map((label, index) => (
              <button
                key={label}
                className={activeMobileGraph === index ? 'active' : ''}
                onClick={() => {
                  if (activeMobileGraph !== index) {
                    setSlideDirection(index < activeMobileGraph ? 'right' : 'left');
                    setTimeout(() => setActiveMobileGraph(index), 250);
                  }
                }}
                aria-label={`Show ${label} Graph`}
              />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="graph-container" ref={evalContainerRef}>
            {shouldShowProgress && (
              <div className="circular-progress-container" data-label="Прогресс по итерациям">
                <div className={`circular-progress ${isOptimizing ? 'active' : ''}`} style={{ '--progress': `${displayProgress}%` } as React.CSSProperties}>
                  <div className="circular-progress-value">{displayProgress.toFixed(1)}%</div>
                </div>
              </div>
            )}
            <h3 className="graph-title">{evaluationTitle}<br/><span className="graph-title-sub">Визуализация</span></h3>
            <div className="graph" ref={evalGraphRef} style={{ transform: `scale(${evalScale})`, transformOrigin: 'top left', width: '100%', height: '100%' }}>
              <IterationVisualizationSVG
                iterationData={iterationData}
                iterations={currentIterations}
                graphSliderValue={currentGraphSliderValue}
                baseColor={graphAccentColor}
                optimizationProgress={progress}
                iterationResults={currentIterationResults}
                isMultiAgent={isMultiAgent}
              />
            </div>
          </div>
          <div className="graph-container" ref={embedContainerRef}>
            {shouldShowProgress && (
              <div className="circular-progress-container" data-label="Движение эмбеддингов">
                <div className={`circular-progress ${isOptimizing ? 'active' : ''}`} style={{ '--progress': `${displayProgress}%` } as React.CSSProperties}>
                  <div className="circular-progress-value">{displayProgress.toFixed(1)}%</div>
                </div>
              </div>
            )}
            <h3 className="graph-title">Эмбеддинги<br/><span className="graph-title-sub">Визуализация</span></h3>
            <div className="graph" ref={embedGraphRef} style={{ transform: `scale(${embedScale})`, transformOrigin: 'top left', width: '100%', height: '100%' }}>
              <EmbeddingVisualizationSVG
                embeddingData={embeddingData}
                iterations={currentIterations}
                graphSliderValue={currentGraphSliderValue}
                baseColor={graphAccentColor}
                optimizationProgress={progress}
                iterationResults={currentIterationResults}
                isMultiAgent={isMultiAgent}
              />
            </div>
          </div>
          {/* Отдельный график качества — намеренно без общей оси с Evaluation: шкалы расходятся
              на порядки, совмещение читалось бы как ложная связь. Показываем только когда метрика
              отбора не quality — иначе он один в один дублирует соседний график Evaluation. */}
          {showQualityGraph && (
            <div className="graph-container">
              <h3 className="graph-title">Качество<br/><span className="graph-title-sub">Визуализация</span></h3>
              <div className="graph">
                <IterationVisualizationSVG
                  iterationData={[]}
                  iterations={currentIterations}
                  graphSliderValue={currentGraphSliderValue}
                  baseColor={graphAccentColor}
                  optimizationProgress={progress}
                  iterationResults={currentIterationResults}
                  isMultiAgent={isMultiAgent}
                  metricKey="quality_point"
                  yAxisLabel="Качество"
                />
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default GraphsSection; 